import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios, { API } from '@/utils/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Mail, Loader2, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_THEME = {
  primary_color: "#3B82F6",
  secondary_color: "#6366F1",
  background_primary: "#0F172A",
  background_secondary: "#1E293B",
  text_primary: "#F8FAFC",
  text_secondary: "#CBD5E1",
  border_color: "#475569"
};

export default function B2BForgotPassword() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [email, setEmail] = useState('');

  useEffect(() => {
    fetchClientInfo();
  }, [clientId]);

  // Apply CSS variables for B2B theme when theme state changes
  useEffect(() => {
    const root = document.documentElement;
    
    // Set CSS variables for global styling (including toasts)
    root.style.setProperty('--theme-primary', theme.primary_color);
    root.style.setProperty('--theme-accent', theme.secondary_color || theme.primary_color);
    root.style.setProperty('--theme-card', theme.background_secondary);
    root.style.setProperty('--theme-text', theme.text_primary);
    root.style.setProperty('--theme-border', theme.border_color);
    
    // Calculate button text color based on primary color luminance
    const hex = theme.primary_color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    root.style.setProperty('--theme-button-text', luminance > 0.5 ? '#1a1a2e' : '#ffffff');
    
    // Cleanup on unmount - reset to defaults
    return () => {
      root.style.removeProperty('--theme-primary');
      root.style.removeProperty('--theme-accent');
      root.style.removeProperty('--theme-card');
      root.style.removeProperty('--theme-text');
      root.style.removeProperty('--theme-border');
      root.style.removeProperty('--theme-button-text');
    };
  }, [theme]);

  const fetchClientInfo = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/b2b/client-info/${clientId}`);
      setClientData(res.data);
      
      if (res.data.theme) {
        setTheme({ ...DEFAULT_THEME, ...res.data.theme });
      } else if (res.data.primary_color) {
        setTheme(prev => ({
          ...prev,
          primary_color: res.data.primary_color,
          secondary_color: res.data.secondary_color || res.data.primary_color
        }));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Client portal not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    try {
      setSubmitting(true);
      await axios.post(`${API}/b2b/forgot-password`, {
        client_id: clientId,
        email: email
      });
      setSubmitted(true);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme.background_primary }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.primary_color }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: theme.background_primary }}>
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h1 className="text-2xl font-bold mb-2" style={{ color: theme.text_primary }}>Portal Not Found</h1>
          <p style={{ color: theme.text_secondary }}>{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ 
          background: theme.background_primary // SOLID color for B2B
        }}
      >
        <div 
          className="w-full max-w-md p-8 rounded-2xl shadow-2xl text-center"
          style={{ 
            background: theme.background_secondary,
            border: `1px solid ${theme.border_color}`
          }}
        >
          <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: theme.primary_color }} />
          <h1 className="text-2xl font-bold mb-4" style={{ color: theme.text_primary }}>Check Your Email</h1>
          <p style={{ color: theme.text_secondary }} className="mb-6">
            If an account exists for {email}, you'll receive a password reset link shortly.
          </p>
          <Button
            onClick={() => navigate(`/portal/${clientId}/login`)}
            className="w-full h-12"
            style={{
              background: theme.primary_color // SOLID color for B2B
            }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ 
        background: theme.background_primary // SOLID color for B2B
      }}
    >
      <div 
        className="w-full max-w-md p-8 rounded-2xl shadow-2xl"
        style={{ 
          background: theme.background_secondary,
          border: `1px solid ${theme.border_color}`
        }}
      >
        {/* Back Button */}
        <button
          onClick={() => navigate(`/portal/${clientId}/login`)}
          className="flex items-center gap-2 mb-6 hover:opacity-80 transition-opacity"
          style={{ color: theme.text_secondary }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          {clientData?.logo_url ? (
            <img 
              src={clientData.logo_url} 
              alt={clientData.name}
              className="h-12 mx-auto mb-4 object-contain"
            />
          ) : null}
          <h1 className="text-2xl font-bold mb-2" style={{ color: theme.text_primary }}>
            Reset Password
          </h1>
          <p style={{ color: theme.text_secondary }}>
            Enter your email and we'll send you a reset link
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: theme.text_secondary }}
            >
              Email Address
            </label>
            <div className="relative">
              <Mail 
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5"
                style={{ color: theme.text_secondary }}
              />
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12"
                style={{
                  background: theme.background_primary,
                  border: `1px solid ${theme.border_color}`,
                  color: theme.text_primary
                }}
                data-testid="b2b-forgot-email"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 text-white font-semibold rounded-lg"
            style={{
              background: theme.primary_color, // SOLID color for B2B
              opacity: submitting ? 0.7 : 1
            }}
            data-testid="b2b-forgot-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Reset Link'
            )}
          </Button>
        </form>

        {/* Powered By */}
        <div className="mt-8 pt-6 border-t text-center" style={{ borderColor: theme.border_color }}>
          <p className="text-xs" style={{ color: theme.text_secondary, opacity: 0.6 }}>
            Powered by UnCloned® Journal
          </p>
        </div>
      </div>
    </div>
  );
}
